// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/FormBuilder.tsx
// EF-6.6: Main drag-and-drop form builder — three-column layout

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import FieldPalette, { FIELD_TYPES, FIELD_ICONS } from './FieldPalette';
import FieldEditor, { type EditableField } from './FieldEditor';
import FormPreview from './FormPreview';
import { intakeFormApi, type IntakeForm, type FormSchema, type FormField, type CreateFormData } from './intakeFormApi';

interface FormBuilderProps {
  formId: string | null;           // null = creating new form
  onClose: () => void;             // return to list
}

function generateLocalId(): string {
  return `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function generateFieldId(type: string, fields: EditableField[]): string {
  const existing = fields.filter(f => f.id.startsWith(type)).length;
  return `${type}_${existing + 1}`;
}

function stripLocalIds(fields: EditableField[]): FormField[] {
  return fields.map(({ _localId, ...rest }) => rest);
}

const FormBuilder: React.FC<FormBuilderProps> = ({ formId, onClose }) => {
  // --- State ---
  const [formName, setFormName] = useState('');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [submitButtonText, setSubmitButtonText] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [fields, setFields] = useState<EditableField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'canvas' | 'preview'>('canvas');
  const [loading, setLoading] = useState(!!formId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baselineRef = useRef<string>('');

  const selectedField = fields.find(f => f._localId === selectedFieldId) || null;

  // --- Load existing form ---
  useEffect(() => {
    if (!formId) {
      setTitle('Untitled Form');
      setFormName('Untitled Form');
      baselineRef.current = JSON.stringify([]);
      return;
    }
    (async () => {
      try {
        const form = await intakeFormApi.getForm(formId);
        setFormName(form.form_name);
        setDescription(form.description || '');
        setTitle(form.form_schema.title);
        setSubmitButtonText(form.form_schema.submit_button_text || '');
        setSuccessMessage(form.form_schema.success_message || '');
        setIsDefault(form.is_default);
        const loaded = form.form_schema.fields.map(f => ({ ...f, _localId: generateLocalId() }));
        setFields(loaded);
        baselineRef.current = JSON.stringify(stripLocalIds(loaded));
      } catch (err: any) {
        setError(err.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  const isDirty = JSON.stringify(stripLocalIds(fields)) !== baselineRef.current;

  // --- DnD handler ---
  const onDragEnd = useCallback((result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    // Palette → Canvas: create new field
    if (source.droppableId === 'palette' && destination.droppableId === 'canvas') {
      const type = draggableId.replace('palette-', '');
      const fieldType = FIELD_TYPES.find(ft => ft.type === type);
      if (!fieldType) return;

      const newField: EditableField = {
        _localId: generateLocalId(),
        id: generateFieldId(type, fields),
        type: fieldType.type,
        label: fieldType.label,
        required: false,
        options: ['select', 'multiselect'].includes(type)
          ? [{ value: 'option_1', label: 'Option 1' }, { value: 'option_2', label: 'Option 2' }]
          : undefined,
      };

      setFields(prev => {
        const next = [...prev];
        next.splice(destination.index, 0, newField);
        return next;
      });
      setSelectedFieldId(newField._localId);
      return;
    }

    // Canvas → Canvas: reorder
    if (source.droppableId === 'canvas' && destination.droppableId === 'canvas') {
      setFields(prev => {
        const next = [...prev];
        const [moved] = next.splice(source.index, 1);
        next.splice(destination.index, 0, moved);
        return next;
      });
    }
  }, [fields]);

  // --- Field editing ---
  const handleFieldChange = useCallback((patch: Partial<EditableField>) => {
    if (!selectedFieldId) return;
    setFields(prev =>
      prev.map(f => (f._localId === selectedFieldId ? { ...f, ...patch } : f)),
    );
  }, [selectedFieldId]);

  const handleFieldRemove = useCallback(() => {
    if (!selectedFieldId) return;
    setFields(prev => prev.filter(f => f._localId !== selectedFieldId));
    setSelectedFieldId(null);
  }, [selectedFieldId]);

  // --- Save ---
  const handleSave = async () => {
    if (!formName.trim()) {
      alert('Form name is required');
      return;
    }
    setSaving(true);
    try {
      const schema: FormSchema = {
        title: title || formName,
        description: description || undefined,
        fields: stripLocalIds(fields),
        submit_button_text: submitButtonText || undefined,
        success_message: successMessage || undefined,
      };
      const data: CreateFormData = {
        form_name: formName,
        description: description || undefined,
        form_schema: schema,
        is_default: isDefault,
      };
      if (formId) {
        await intakeFormApi.updateForm(formId, data);
      } else {
        await intakeFormApi.createForm(data);
      }
      baselineRef.current = JSON.stringify(stripLocalIds(fields));
      onClose();
    } catch (err: any) {
      alert(err.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  // --- Loading / Error ---
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={onClose} className="text-sm text-lex-accent hover:underline">Back to list</button>
      </div>
    );
  }

  // --- Build preview schema ---
  const previewSchema: FormSchema = {
    title: title || formName || 'Untitled Form',
    description: description || undefined,
    fields: stripLocalIds(fields),
    submit_button_text: submitButtonText || undefined,
    success_message: successMessage || undefined,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          &larr; Back
        </button>
        <div className="flex-1 flex items-center gap-3">
          <input
            type="text"
            value={formName}
            onChange={e => setFormName(e.target.value)}
            placeholder="Form name"
            className="text-sm font-semibold text-gray-900 dark:text-white bg-transparent border-none outline-none focus:ring-0 w-48"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent h-3.5 w-3.5"
            />
            Default
          </label>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
          <button
            onClick={() => setActiveTab('canvas')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              activeTab === 'canvas'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Canvas
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              activeTab === 'preview'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Preview
          </button>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-md hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Form metadata */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="grid grid-cols-2 gap-3 max-w-2xl">
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Form Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Displayed at top of form"
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description"
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Submit Button Text</label>
            <input
              type="text"
              value={submitButtonText}
              onChange={e => setSubmitButtonText(e.target.value)}
              placeholder="Submit"
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Success Message</label>
            <input
              type="text"
              value={successMessage}
              onChange={e => setSuccessMessage(e.target.value)}
              placeholder="Your submission has been received."
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Three-column body */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Field Palette */}
          <FieldPalette />

          {/* Center: Canvas or Preview */}
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {activeTab === 'preview' ? (
              <div className="p-6">
                <FormPreview schema={previewSchema} />
              </div>
            ) : (
              <Droppable droppableId="canvas">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-full p-4 space-y-2 transition-colors ${
                      snapshot.isDraggingOver ? 'bg-lex-accent-soft dark:bg-lex-accent-soft' : ''
                    }`}
                  >
                    {fields.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-center py-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          Drag fields from the palette to build your form
                        </p>
                      </div>
                    )}

                    {fields.map((field, index) => (
                      <Draggable key={field._localId} draggableId={field._localId} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            onClick={() => setSelectedFieldId(field._localId)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${
                              dragSnapshot.isDragging
                                ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft shadow-lg'
                                : selectedFieldId === field._localId
                                ? 'border-lex-accent bg-white dark:bg-gray-800 ring-2 ring-lex-accent dark:ring-lex-accent'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            {/* Drag handle indicator */}
                            <span className="text-gray-300 dark:text-gray-600 select-none">⋮⋮</span>
                            {/* Icon */}
                            <span className="w-5 text-center text-xs text-gray-400">
                              {FIELD_ICONS[field.type]}
                            </span>
                            {/* Label + type */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {field.label || 'Untitled'}
                              </p>
                              <p className="text-[10px] text-gray-400">{field.type}{field.required ? ' · required' : ''}</p>
                            </div>
                            {/* Badges */}
                            {field.conditional_logic && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 rounded">
                                Conditional
                              </span>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </div>

          {/* Right: Field Editor */}
          {selectedField && activeTab === 'canvas' && (
            <FieldEditor
              field={selectedField}
              allFields={fields}
              onChange={handleFieldChange}
              onRemove={handleFieldRemove}
            />
          )}
        </div>
      </DragDropContext>
    </div>
  );
};

export default FormBuilder;
